import * as Sentry from "@sentry/react";
import {
  QueryClientProvider as BaseQueryClientProvider,
  MutationCache,
  QueryCache,
  QueryClient,
} from "@tanstack/react-query";
import * as React from "react";
import { createContext } from "react-router";
import { AssertError } from "typebox/value";
import {
  isLoggedInStatusQuery,
  sessionWasLost,
  trackLoggedInStatus,
} from "@/shared/queries/loggedInStatus.ts";
import { FetchAborted, FetchError } from "@/shared/util/fetch.ts";
import { getCvPartner } from "@/shared/util/getCvPartner.ts";

// These statuses are pure functions of the request we sent: a malformed
// request stays malformed, a body that fails validation stays invalid, and a
// session or permission we lack now we still lack one backoff later. Unlike
// e.g. 404 (a lagging Elasticsearch index catches up between attempts) and 429
// (the rate-limit window passes), retrying cannot change the answer - it only
// delays the error.
const unretryableStatuses = new Set([400, 401, 403, 422]);

function skipUnretryableStatuses(failureCount: number, error: unknown) {
  if (error instanceof FetchError && unretryableStatuses.has(error.status)) {
    return false;
  }

  // The response parsed fine but does not match our schema. The server will
  // answer the same way on the next attempt.
  if (error instanceof AssertError) {
    return false;
  }

  return failureCount < 3;
}

export function createQueryClient(): QueryClient {
  // eslint-disable-next-line prefer-const -- assigned once below; the caches' callbacks must reference the client they end up belonging to
  let client: QueryClient;

  const queryCache = new QueryCache({
    onError: (error, query) => {
      const { queryKey, queryHash, meta } = query;

      if (meta?.skipErrorLogging === true) return;
      if (error instanceof FetchAborted) return;

      const report = () => {
        const extra = { queryKey };
        extractTypeBoxError(error, extra);

        const status = error instanceof FetchError ? error.status : undefined;
        const queryName = queryKey.at(0)?.toString() ?? "unknown";

        getCvPartner().faro?.api.pushError(error as Error, {
          context: {
            queryHash,
            query: queryName,
            ...(status != null && { status: String(status) }),
          },
        });

        Sentry.captureException(error, {
          extra,
          tags: {
            query: queryName,
            ...(status != null && { status: String(status) }),
          },
        });
      };

      if (isLoggedInStatusQuery(query)) {
        report();
        return;
      }

      void reportUnlessSessionWasLost(client, error, report);
    },
  });

  const mutationCache = new MutationCache({
    onError: (error, _, __, { meta, options: { mutationKey } }) => {
      if (meta?.skipErrorLogging === true) return;
      if (error instanceof FetchAborted) return;

      void reportUnlessSessionWasLost(client, error, () => {
        const extra = { mutationKey };
        extractTypeBoxError(error, extra);

        const status = error instanceof FetchError ? error.status : undefined;
        const mutation = mutationKey?.at(0)?.toString() ?? "unknown";

        getCvPartner().faro?.api.pushError(error as Error, {
          context: {
            mutation:
              mutationKey?.map((k) => JSON.stringify(k)).join(",") ?? "unknown",
            query: `mutation:${mutation}`,
            ...(status != null && { status: String(status) }),
          },
        });

        Sentry.captureException(error, {
          extra,
          tags: { mutation, ...(status != null && { status: String(status) }) },
        });
      });
    },
  });

  client = new QueryClient({
    defaultOptions: {
      queries: {
        // Stale time defaults to 0 - giving it a higher value lets minor
        // waterfall requests be served from cache
        // https://tanstack.com/query/v5/docs/framework/react/guides/important-defaults
        staleTime: 150,
        retry: skipUnretryableStatuses,
      },
    },
    queryCache,
    mutationCache,
  });

  trackLoggedInStatus(client);

  return client;
}

// TODO: remove export
export const queryClient = createQueryClient();

// Not awaited: query-core awaits the mutation cache's onError, so awaiting
// here would hold every failing mutation's own callbacks behind the request.
async function reportUnlessSessionWasLost(
  client: QueryClient,
  error: unknown,
  report: () => void,
) {
  const isSessionLost = await sessionWasLost(client, error);

  if (isSessionLost) return;

  report();
}

function extractTypeBoxError(error: unknown, extra: Record<string, unknown>) {
  if (error instanceof AssertError) {
    extra.violations = error.cause.errors;
  }
}

export const QueryClientProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <BaseQueryClientProvider client={queryClient}>
    {children}
  </BaseQueryClientProvider>
);

// eslint-disable-next-line @eslint-react/naming-convention-context-name
export const queryClientContext = createContext(queryClient);
